import {html, LitElement, styleMap} from '../libs/lit.dist.js';
import {getEventPosition} from "../util/browser.js";

const PREVENT_TAG_NAME = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];


/**
 * Pozwala na sortowanie elementów wewnątrz.
 * Kolejność posortowanych elementów HTML można pobrać z właściwości "sortedItems", dodatkowo można przekazać dodatkowe informacje
 * w właściwości "ref" elementu - wtedy będą one dostępne pod właściwością "itemsByRef"
 *
 * Atrybuty:
 *  {string}   selector - Selector do wyboru sortowanych elementów (domyślnie "[sortable-item]")
 *  {string}   selector - Selector do wyboru kontenera, który zwiera sortowane elementy (domyślnie "[sortable-parent]")
 *  {string}   handle   - Selector do wyboru uchwytu, który pozwala na sortowanie. Jeśli pusty to uchwytem jest cały element (domyślnie "")
 *
 *  Zdarzenia:
 *   sort - zdarzenie po posortowaniu elementów. Jeśli event zostanie anulowany przez preventDefault(), to kolejność elementów HTML nie zostanie zmieniona
 *
 * Przykład użycia w HTML:
 *  <pk-sortable id="sorted">
 *      <ul sortable-parent>
 *          <li sortable-item>...</li>
 *          ...
 *      </ul>
 *  </pk-sortable>
 *  jQuery('#sorted').on('sort', e => ...);
 *
 * Przykład użycia w LIT:
 *  <pk-sortable @sort="{e => {
 *      e.preventDefault();
 *      model.setItems(e.target.itemsByRef);
 *  }">
 *      <ul sortable-parent>
 *          ${items.map(model.item => html`
 *              <li .ref="${item}" sortable-item>...</li>
 *          `)}
 *      </ul>
 *  </pk-sortable>
 *
 */
export class PkSortable extends LitElement {

    static currentDragElement;
    static currentDragElementHeight;

    get draggableSlot() {
        return this.renderRoot.querySelector('slot');
    }

    get draggableParent() {
        const nodes = this.draggableSlot.assignedNodes({flatten: true});
        let parentNode;
        for (let node of nodes) {
            if (node.nodeType === 1) {
                if (node.matches(this.parentSelector)) {
                    parentNode = node;
                } else {
                    parentNode = node.querySelector(this.parentSelector);
                }
                if (parentNode) {
                    return parentNode;
                }
            }
        }
        return null;
    }

    get items() {
        return this.getDraggables();
    }

    get itemsByRef() {
        return (this.sortedItems || this.items).map(item => {
            if (item) {
                return typeof item.ref === 'function' ? item.ref() : item.ref
            }
            return void 0;
        });
    }

    static get properties() {
        return {
            selector: {type: String},
            parentSelector: {type: String},
            handle: {type: String},
            preventDropOnParent: {type: Boolean},
            disabled: {type: Boolean},
            horizontal: {type: Boolean},
        };
    }

    constructor() {
        super();
        this.itemsEvents = [];
        this.selector = '[sortable-item]';
        this.parentSelector = '[sortable-parent]';
        this.handle = '';
        this.preventDropOnParent = false;
        this.disabled = false;
        this.horizontal = false;
        this.initDropper();
    }

    initDropper() {
        this.dropper = document.createElement('div');
        this.dropper.style.display = 'none';
        this.dropper.style.borderRadius = 'var(--border-radius, 4px)';
        this.dropper.style.outline = 'var(--border-width, 2px) var(--border-color, rgba(0,87,255,0.3)) dashed';
        this.dropper.style.position = 'absolute';
        this.dropper.style.zIndex = '1050';
        this.dropper.style.background = 'var(--bg-color, rgba(0,87,255,0.15))';
        this.dropper.classList.add('pk-draggable--dropper');
    }

    firstUpdated() {
        this.appendChild(this.dropper);
        this.observeItems();
        this.updateItems();
        this.bindDomEvents();

        this.onGlobalDrop = () => {
            this.dropper.style.display = 'none';
        };
        document.addEventListener('drop', this.onGlobalDrop);

        this.onGlobalTouchEnd = (e) => this.onTouchEnd(e);
        document.addEventListener('touchend', this.onGlobalTouchEnd);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.unobserveItems();
        this.cleanupItems();
        this.dropper.remove();
        document.removeEventListener('drop', this.onGlobalDrop);
        document.removeEventListener('touchend', this.onGlobalTouchEnd);
    }

    render() {
        return html`
            <div
                style="${styleMap({
                    position: 'relative',
                    height: 'var(--inner-height, auto)',
                })}"
                @dragend="${e => this.onDragEnd(e)}"
                @dragover="${e => this.onDragenter(e)}"
                @dragleave="${e => this.onDragleave(e)}"
                @touchend="${e => this.onTouchEnd(e)}"
                @touchcancel="${e => this.onDragEnd(e)}"
                @touchmove="${e => this.onTouchmove(e)}"
                @touchleave="${e => this.onDragleave(e)}"
            >
                <slot></slot>
            </div>
        `;
    }

    getEventPosition(e) {
        return getEventPosition(e);
    }

    onDragenter(e) {
        const [x, y] = this.getEventPosition(e);
        const elements = document.elementsFromPoint(x, y);

        let el, dropElement;
        for (el of elements) {
            // Wyjątek dla tabel (elementsFromPoint nie zwraca elementów <tr>)
            if ((el.tagName === 'TD' || el.tagName === 'TH') && el.parentElement.tagName === 'TR') {
                el = el.parentElement;
            }
            if (el.matches(this.selector)) {
                dropElement = el;
                break;
            }
        }

        const parentEl = this.draggableParent;
        if (!dropElement && !this.preventDropOnParent) {
            dropElement = parentEl;
        }

        if (!dropElement) {
            return;
        }

        const notCancelled = this.dispatchEvent(new Event('pk-sorter:dragenter'));
        if (notCancelled === false) {
            return;
        }

        if (dropElement === this.dropNode) {
            return;
        }

        this.dropNode?.classList.remove('pk-draggable--current-drop-target');
        const position = dropElement.getBoundingClientRect();
        const positionParent = this.getBoundingClientRect();
        this.dropper.style.display = 'block';
        this.dropper.style.top = `${Math.abs(position.top - positionParent.top)}px`;
        this.dropper.style.left = `${Math.abs(position.left - positionParent.left)}px`;
        this.dropper.style.width = `${position.width}px`;
        this.dropper.style.height = `${position.height}px`;

        if (dropElement === parentEl) {
            this.dropper.classList.add('pk-draggable--dropper-drop-on-parent');
        } else {
            this.dropper.classList.remove('pk-draggable--dropper-drop-on-parent');
        }

        this.dropNode = dropElement;
        this.dropNode.classList.add('pk-draggable--current-drop-target');
        this.updateElementsClassList();
        this.draggableParent?.classList?.add('is-sorting');
    }

    onTouchmove(e) {
        if (PkSortable.currentDragElement) {
            e.preventDefault();
            this.onDragenter(e);
        }
    }

    onDragleave(e) {
        const [x, y] = this.getEventPosition(e);
        const el = document.elementFromPoint(x, y);
        if (el !== this.dropper) {
            this.hideDropperElement();
            this.draggableParent?.classList?.remove('is-sorting');
        }
    }

    getDraggables() {
        if (!this.draggableSlot) {
            return [];
        }
        const nodes = this.draggableSlot.assignedNodes({flatten: true});
        let items = [];
        for (let node of nodes) {
            if (node.nodeType === 1) {
                items = [
                    ...items,
                    ...node.querySelectorAll(this.selector),
                ];
            }
        }
        return items;
    }

    getElementSorter(element) {
        while (element) {
            if (element instanceof PkSortable) {
                return element;
            }
            element = element.parentNode;
        }
    }

    bindDomEvents() {
        this.dropper.addEventListener('dragover', e => {
            e.preventDefault();
        });
        this.dropper.addEventListener('drop', this.onDropHandler.bind(this));
    }

    onDropHandler(e) {
        const [x, y] = this.getEventPosition(e);
        const dropItem = this.getDraggableAtPoint(x, y);
        const items = [...this.getDraggables()];
        const fromIndex = items.indexOf(PkSortable.currentDragElement);
        let toIndex = items.indexOf(dropItem);

        if (fromIndex > -1) {
            items.splice(fromIndex, 1);
        }
        if (toIndex === -1) {
            items.push(PkSortable.currentDragElement);
        } else {
            if (this.horizontal && toIndex > fromIndex && fromIndex > 0 && toIndex > 0) {
                toIndex--;
            }
            items.splice(toIndex, 0, PkSortable.currentDragElement);
        }

        this.sortedItems = items;
        const itemSorter = this.getElementSorter(PkSortable.currentDragElement);
        if (itemSorter && itemSorter !== this) {
            itemSorter.sortedItems = [...itemSorter.getDraggables()].filter(item => item !== PkSortable.currentDragElement);
            const externalNotCancelled = itemSorter.dispatchEvent(this.createSortEvent(items));
            if (externalNotCancelled) {
                itemSorter.updateItemsPositions();
            }
        }

        const notCancelled = this.dispatchEvent(this.createSortEvent(items));
        if (notCancelled) {
            this.updateItemsPositions();
        }
    }

    createSortEvent(items) {
        return new CustomEvent('sort', {
            cancelable: true,
            composed: true,
            bubbles: true,
            detail: items
        });
    }

    updateItemsPositions() {
        const parent = this.draggableParent;
        if (!this.sortedItems || !parent) {
            return;
        }
        for (let item of this.sortedItems) {
            if (item) {
                parent.appendChild(item);
            }
        }
    }

    getDraggableAtPoint(x, y) {
        const elements = document.elementsFromPoint(x, y);
        for (let el of elements) {
            if ((el.tagName === 'TD' || el.tagName === 'TH') && el.parentElement.tagName === 'TR') {
                el = el.parentElement;
            }
            if (el.matches(this.selector)) {
                return el;
            }
        }
    }

    onTouchEnd(e) {
        this.onDropHandler(e);
        this.hideDropperElement();
        this.onDragEnd();
    }

    hideDropperElement() {
        this.dropper.classList.remove('pk-draggable--dropper-drop-on-parent');
        this.dropper.style.display = 'none';
        if (this.dropNode) {
            this.dropNode.classList.remove('pk-draggable--current-drop-target');
            this.dropNode = null;
        }
    }

    onDragEnd() {
        this.dropper.style.display = 'none';
        PkSortable.currentDragElement?.classList?.remove('pk-draggable--current-drag-element');
        PkSortable.currentDragElement = null;

        if (this.dropNode) {
            this.dropNode.classList.remove('pk-draggable--current-drop-target');
            this.dropNode = null;
        }
        this.draggableParent?.classList?.remove('is-sorting');
        this.dispatchEvent(new Event('sortEnd', {bubbles: true}));
        this.onSortEnd();
    }

    bindDraggableElement(element, event, handler) {
        if (element && event && handler) {
            element.addEventListener(event, handler);
            this.itemsEvents.push({
                element,
                event,
                handler,
            });
        }
    }

    updateItems() {
        if (this.disabled) {
            return;
        }

        const items = this.getDraggables();
        for (let item of items) {
            if (!item.pkSortableInit) {
                const dragStartElement = this.handle
                    ? (item.querySelector(this.handle) || item)
                    : item;

                const dragHandler = e => {
                    if (!dragStartElement.dispatchEvent(new Event('beforeSortStart', {bubbles: true, cancelable: true}))) {
                        e.preventDefault();
                        return;
                    }

                    const [x, y] = this.getEventPosition(e);
                    const el = document.elementFromPoint(x, y);
                    if (el && PREVENT_TAG_NAME.indexOf(el.tagName) !== -1) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }

                    if (el && this.handle && !el.closest(this.handle)) {
                        e.preventDefault();
                        return;
                    }

                    dragStartElement.dispatchEvent(new Event('sortStart', {bubbles: true}));
                    if (e.dataTransfer) {
                        e.dataTransfer.setDragImage(item, 0, 0);
                        e.dataTransfer.dropEffect = "move";
                    }
                    PkSortable.currentDragElement = item;
                    PkSortable.currentDragElement.classList.add('pk-draggable--current-drag-element');
                    this.onSortStart();
                }

                this.bindDraggableElement(dragStartElement, 'dragstart', dragHandler);
                this.bindDraggableElement(dragStartElement, 'touchmove', e => {
                    if (!PkSortable.currentDragElement) {
                        dragHandler(e);
                    }
                });

                dragStartElement.draggable = true;
                item.pkSortableInit = true;
            }
        }
    }

    updateElementsClassList() {
        let isBefore = true;
        for (let el of this.getDraggables()) {
            el.classList.remove('pk-draggable--drag-element', 'pk-draggable--drag-element-before', 'pk-draggable--drag-element-after');
            if (PkSortable.currentDragElement) {
                el.classList.add('pk-draggable--drag-element');
                if (el === this.dropNode) {
                    isBefore = false;
                }

                if (el === PkSortable.currentDragElement) {
                    el.classList.add('pk-draggable--current-drag-element');
                }

                if (isBefore) {
                    el.classList.add('pk-draggable--drag-element-before');
                } else if (el !== this.dropNode) {
                    el.classList.add('pk-draggable--drag-element-after');
                }
            }
        }
    }

    onSortStart() {
        this.updateElementsClassList();
    }

    onSortEnd() {
        this.updateElementsClassList();

    }

    cleanupItems() {
        for (let row of this.itemsEvents) {
            row.element.removeEventListener(row.event, row.handler);
        }
    }

    observeItems() {
        if (!this.observer) {
            this.observer = new MutationObserver(_ => {
                this.updateItems();
            });
            this.observer.observe(this, {childList: true, subtree: true});
        }
    }

    unobserveItems() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

}

customElements.define('pk-sortable', PkSortable);
